// Homepage: the mobile hero carousel, the product rail and fade-in sections.
(() => {
  // On phones and small tablets the hero tiles become a revolving carousel:
  // the focused tile sits in front and the others swing round from behind.
  const carousel = document.querySelector('[data-hero-carousel]');
  const dotsBar = document.querySelector('[data-hero-dots]');

  if (carousel && dotsBar) {
    const tiles = [...carousel.querySelectorAll('[data-hero-tile]')];
    const dots = [...dotsBar.querySelectorAll('[data-hero-dot]')];
    const count = tiles.length;
    const compact = window.matchMedia('(max-width: 960px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let position = 0;
    let frame = 0;
    let enabled = false;

    const wrap = (value) => ((value % count) + count) % count;
    const activeIndex = () => wrap(Math.round(position));

    // Offset of each tile from the front, wrapped into [-count / 2, count / 2).
    const offsetOf = (index) => wrap(index - position + count / 2) - count / 2;

    const render = () => {
      tiles.forEach((tile, index) => {
        const offset = offsetOf(index);
        const distance = Math.abs(offset);
        const fade = distance <= 1 ? 1 : Math.max(0, 1 - (distance - 1) * 2);
        tile.style.transform =
          `translateX(${offset * 62}%) translateZ(${distance * -180}px) ` +
          `rotateY(${offset * 38}deg) scale(${1 - Math.min(distance, 1) * 0.1})`;
        tile.style.opacity = String(fade);
        tile.style.filter = `brightness(${1 - Math.min(distance, 1) * 0.45})`;
        tile.style.zIndex = String(10 - Math.round(distance * 4));
        tile.classList.toggle('is-active', distance < 0.5);
      });
      const active = activeIndex();
      dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
    };

    const animateTo = (target) => {
      cancelAnimationFrame(frame);
      const start = position;
      const startedAt = performance.now();
      const duration = reduceMotion.matches ? 0 : 560;
      const step = (now) => {
        const progress = duration ? Math.min(1, (now - startedAt) / duration) : 1;
        const eased = 1 - (1 - progress) ** 3;
        position = start + (target - start) * eased;
        render();
        if (progress < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };

    // Rotate the shortest way round to a tile.
    const goTo = (index) => {
      const delta = wrap(index - Math.round(position) + Math.floor(count / 2)) - Math.floor(count / 2);
      animateTo(Math.round(position) + delta);
    };

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startPosition = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    let dragging = false;
    let suppressClick = false;

    carousel.addEventListener('pointerdown', (event) => {
      if (!enabled || event.button !== 0) return;
      cancelAnimationFrame(frame);
      pointerId = event.pointerId;
      startX = lastX = event.clientX;
      startY = event.clientY;
      startPosition = position;
      lastTime = event.timeStamp;
      velocity = 0;
      dragging = false;
      suppressClick = false;
    });

    carousel.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!dragging) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          pointerId = null;
          return;
        }
        if (Math.abs(dx) < 8) return;
        dragging = true;
        suppressClick = true;
        carousel.setPointerCapture(event.pointerId);
      }
      const width = tiles[0].offsetWidth || carousel.offsetWidth;
      const elapsed = Math.max(1, event.timeStamp - lastTime);
      velocity = (event.clientX - lastX) / elapsed;
      lastX = event.clientX;
      lastTime = event.timeStamp;
      position = startPosition - dx / width;
      render();
    });

    const release = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      const width = tiles[0].offsetWidth || carousel.offsetWidth;
      const projected = position - (velocity * 180) / width;
      animateTo(Math.max(startPosition - 1, Math.min(startPosition + 1, Math.round(projected))));
    };
    carousel.addEventListener('pointerup', release);
    carousel.addEventListener('pointercancel', release);

    // A drag never follows a link; a tap on a side tile brings it to the front.
    carousel.addEventListener(
      'click',
      (event) => {
        if (!enabled) return;
        const tile = event.target.closest('[data-hero-tile]');
        if (suppressClick || (tile && !tile.classList.contains('is-active'))) {
          event.preventDefault();
          if (!suppressClick && tile) goTo(tiles.indexOf(tile));
        }
        suppressClick = false;
      },
      true,
    );

    carousel.addEventListener('focusin', (event) => {
      const tile = event.target.closest('[data-hero-tile]');
      if (enabled && tile && !tile.classList.contains('is-active')) goTo(tiles.indexOf(tile));
    });

    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));

    const sync = () => {
      enabled = compact.matches;
      carousel.classList.toggle('is-carousel', enabled);
      dotsBar.hidden = !enabled;
      cancelAnimationFrame(frame);
      if (enabled) {
        position = activeIndex();
        render();
        return;
      }
      tiles.forEach((tile) => {
        tile.removeAttribute('style');
        tile.classList.remove('is-active');
      });
    };

    compact.addEventListener('change', sync);
    // Some browsers skip the media query event on rotation or zoom, so resize double-checks it.
    window.addEventListener('resize', () => {
      if (compact.matches !== enabled) sync();
    });
    sync();
  }

  // Scroll the product rail by roughly one viewport of cards.
  const rail = document.querySelector('[data-rail]');
  const step = (direction) => rail?.scrollBy({ left: direction * rail.clientWidth * 0.8, behavior: 'smooth' });
  document.querySelector('[data-rail-prev]')?.addEventListener('click', () => step(-1));
  document.querySelector('[data-rail-next]')?.addEventListener('click', () => step(1));

  // Fade sections in once as they reach the viewport.
  const revealables = document.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    revealables.forEach((element) => observer.observe(element));
  } else {
    revealables.forEach((element) => element.classList.add('is-visible'));
  }
})();
